import React, { useState, useRef, useEffect } from "react";
import CarouselArrows from "../ui/CarouselArrows";

function OnboardingStep({
  steps = [],
  currentStep = 0,
  title = "",
  headerLeftContent = null,
}) {
  const [scrollPosition, setScrollPosition] = useState(0);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const containerRef = useRef(null);
  const stepsContainerRef = useRef(null);
  const stepRefs = useRef([]);

  // Check if scrolling is needed and update arrow visibility
  const updateArrowVisibility = () => {
    if (containerRef.current && stepsContainerRef.current) {
      const container = containerRef.current;
      const stepsContainer = stepsContainerRef.current;

      const scrollWidth = stepsContainer.scrollWidth;
      const clientWidth = stepsContainer.clientWidth;
      const isScrollable = scrollWidth > clientWidth;

      setShowLeftArrow(isScrollable && scrollPosition > 0);
      setShowRightArrow(
        isScrollable && scrollPosition < scrollWidth - clientWidth,
      );
    }
  };

  // Scroll functions
  const scrollLeft = () => {
    if (stepsContainerRef.current) {
      const scrollAmount = 200;
      const newPosition = Math.max(0, scrollPosition - scrollAmount);
      stepsContainerRef.current.scrollTo({
        left: newPosition,
        behavior: "smooth",
      });
      setScrollPosition(newPosition);
    }
  };

  const scrollRight = () => {
    if (stepsContainerRef.current) {
      const scrollAmount = 200;
      const maxScroll =
        stepsContainerRef.current.scrollWidth -
        stepsContainerRef.current.clientWidth;
      const newPosition = Math.min(maxScroll, scrollPosition + scrollAmount);
      stepsContainerRef.current.scrollTo({
        left: newPosition,
        behavior: "smooth",
      });
      setScrollPosition(newPosition);
    }
  };

  // Update arrow visibility on scroll
  useEffect(() => {
    const stepsContainer = stepsContainerRef.current;
    if (stepsContainer) {
      const handleScroll = () => {
        setScrollPosition(stepsContainer.scrollLeft);
      };

      stepsContainer.addEventListener("scroll", handleScroll);
      updateArrowVisibility();

      return () => stepsContainer.removeEventListener("scroll", handleScroll);
    }
  }, [scrollPosition]);

  // Update arrow visibility when steps change or window resizes
  useEffect(() => {
    const timer = setTimeout(() => {
      updateArrowVisibility();
    }, 100);

    window.addEventListener("resize", updateArrowVisibility);

    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", updateArrowVisibility);
    };
  }, [steps]);

  // Auto-scroll to current step when it changes
  useEffect(() => {
    if (stepRefs.current[currentStep] && stepsContainerRef.current) {
      const stepElement = stepRefs.current[currentStep];
      const container = stepsContainerRef.current;

      // Calculate the scroll position to center the current step
      const stepLeft = stepElement.offsetLeft;
      const stepWidth = stepElement.offsetWidth;
      const containerWidth = container.clientWidth;

      // Center the step in the container
      const scrollTo = stepLeft - containerWidth / 2 + stepWidth / 2;

      container.scrollTo({
        left: Math.max(0, scrollTo),
        behavior: "smooth",
      });
    }
  }, [currentStep]);

  const getStepStatus = (index) => {
    if (index < currentStep) return "done";
    if (index === currentStep) return "current";
    return "future";
  };

  const getSeparatorStatus = (index) => {
    if (index < currentStep) return "done-to-current";
    if (index === currentStep) return "current-to-future";
    return "future-to-future";
  };

  return (
    <>
      {title && (
        <div className="segment-menu-header">
          <h2>{title}</h2>
          <br></br>
        </div>
      )}
      <div className="ich-wrapper">
        <div className="internal-content-header" ref={containerRef}>
          {/* Header with breadcrumb and arrows */}
          {(showLeftArrow || showRightArrow || headerLeftContent) && (
            <div
              className="onboarding-step-header"
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                paddingBottom: "10px",
              }}
            >
              {/* Left container - breadcrumb or custom content */}
              {headerLeftContent && (
                <div
                  className="onboarding-header-left"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    marginRight: "auto",
                  }}
                >
                  {headerLeftContent}
                </div>
              )}

              {/* Carousel Arrows - right side */}
              {(showLeftArrow || showRightArrow) && (
                <div
                  className="onboarding-step-arrows"
                  style={{ marginLeft: "auto" }}
                >
                  <CarouselArrows
                    onLeftClick={scrollLeft}
                    onRightClick={scrollRight}
                    isLeftDisabled={!showLeftArrow}
                    isRightDisabled={!showRightArrow}
                    size="small"
                    variant="outline"
                  />
                </div>
              )}
            </div>
          )}

          {/* Steps Structure */}
          <div className="onboarding-steps-wrapper" ref={stepsContainerRef}>
            {steps.map((step, index) => {
              const status = getStepStatus(index);
              const separatorStatus =
                index < steps.length - 1 ? getSeparatorStatus(index) : null;

              return (
                <React.Fragment key={index}>
                  <div
                    ref={(el) => (stepRefs.current[index] = el)}
                    className={`onboarding-step-item onboarding-step-${status}`}
                  >
                    <div className="onboarding-step-number">
                      {step.number || index + 1}
                    </div>
                    <span className="onboarding-step-text">
                      {step.text || step}
                    </span>
                  </div>
                  {separatorStatus && (
                    <div
                      className={`onboarding-step-separator onboarding-separator-${separatorStatus}`}
                    ></div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
}

export default OnboardingStep;
